import { useCallback, useEffect, useRef } from "react";
import { isRole } from "@toorak/tc-common-fe-sdk";
import { updateDrawDetails } from "../../../stores/drawRequest/drawRequest.action";
import { DrawRolesEnum } from "../../../utils/constants";
import { getBankDetailsSaveWarning } from "./bankAddressValidation";
import {
  buildDrawAutosaveFingerprint,
  getAutosaveDelay,
  RefLike,
  shouldSkipDraftAutosaveSchedule
} from "./drawLoanInformation.helpers";

export function runDraftDrawAutosave(params: {
  getState: () => any;
  dispatch: (action: any) => void;
  drawId: string;
  isOrig: boolean;
  draft: string;
  lastPutFingerprintRef: RefLike<string>;
  lastPutAtRef: RefLike<number>;
}) {
  try {
    const state = params.getState();
    const currentDraw = state?.drawRequestStore?.drawDetails;
    if (
      currentDraw?.stage !== params.draft ||
      isRole(DrawRolesEnum.ORIGINATOR_VIEW_DRAW)
    ) {
      return;
    }
    if (!currentDraw?.primaryLoanId) return;
    const selectedBank = state?.drawRequestStore?.selectedBankDetails;
    if (
      getBankDetailsSaveWarning(
        currentDraw,
        selectedBank,
        state?.drawRequestStore?.visibleRouting
      )
    ) {
      return;
    }
    const fingerprint = buildDrawAutosaveFingerprint(
      currentDraw.primaryLoanId,
      currentDraw.drawAmountDetails,
      currentDraw.propertyDetails,
      selectedBank
    );
    if (fingerprint === params.lastPutFingerprintRef.current) return;
    params.dispatch(
      updateDrawDetails(
        currentDraw,
        selectedBank,
        params.drawId,
        params.isOrig,
        currentDraw.stage,
        currentDraw.originatorId,
        { showLoader: false }
      )
    );
    params.lastPutFingerprintRef.current = fingerprint;
    params.lastPutAtRef.current = Date.now();
  } catch (error) {
    console.error("Error in dispatching updateDrawDetails:", error);
  }
}

export function useDraftDrawAutosave(params: {
  dispatch: (action: any) => void;
  storeRef: RefLike<{ getState: () => any }>;
  drawId: string;
  isOrig: boolean;
  draft: string;
  drawDetails: any;
  selectedBankDetails: any;
  selectedProperty: any[];
}) {
  const {
    dispatch,
    storeRef,
    drawId,
    isOrig,
    draft,
    drawDetails,
    selectedBankDetails,
    selectedProperty
  } = params;
  const lastPutFingerprintRef = useRef<string>("");
  const lastPutAtRef = useRef<number>(0);
  const pendingSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const hasPendingChangesRef = useRef(false);

  const runSave = useCallback(() => {
    runDraftDrawAutosave({
      getState: () => storeRef.current.getState(),
      dispatch,
      drawId,
      isOrig,
      draft,
      lastPutFingerprintRef,
      lastPutAtRef
    });
  }, [dispatch, drawId, isOrig, draft, storeRef]);

  const runSaveRef = useRef(runSave);
  runSaveRef.current = runSave;

  const drawAmountDetailsKey = JSON.stringify(
    drawDetails?.drawAmountDetails ?? {}
  );
  const bankDetailsKey = JSON.stringify(selectedBankDetails ?? {});
  const currentFingerprint = buildDrawAutosaveFingerprint(
    drawDetails?.primaryLoanId,
    drawDetails?.drawAmountDetails,
    drawDetails?.propertyDetails,
    selectedBankDetails
  );

  useEffect(() => {
    return () => {
      if (hasPendingChangesRef.current) {
        hasPendingChangesRef.current = false;
        runSaveRef.current();
      }
    };
  }, []);

  useEffect(() => {
    const scheduleAction = shouldSkipDraftAutosaveSchedule({
      stage: drawDetails?.stage,
      draft,
      isOriginatorViewDraw: isRole(DrawRolesEnum.ORIGINATOR_VIEW_DRAW),
      lastPutFingerprint: lastPutFingerprintRef.current,
      currentFingerprint
    });
    if (scheduleAction === "skip") {
      hasPendingChangesRef.current = false;
      return undefined;
    }
    if (scheduleAction === "prime") {
      lastPutFingerprintRef.current = currentFingerprint;
      hasPendingChangesRef.current = false;
      return undefined;
    }

    hasPendingChangesRef.current = true;
    const delay = getAutosaveDelay(Date.now() - lastPutAtRef.current);
    pendingSaveTimeoutRef.current = setTimeout(() => {
      pendingSaveTimeoutRef.current = null;
      hasPendingChangesRef.current = false;
      runSave();
    }, delay);

    return () => {
      if (pendingSaveTimeoutRef.current !== null) {
        clearTimeout(pendingSaveTimeoutRef.current);
        pendingSaveTimeoutRef.current = null;
      }
    };
  }, [
    selectedProperty,
    drawDetails?.primaryLoanId,
    drawAmountDetailsKey,
    bankDetailsKey,
    drawDetails?.stage,
    draft,
    runSave,
    currentFingerprint
  ]);
}
